/**
 * Cumple — Design system shared (Sprint 10).
 *
 * Componentes UI que se repiten en páginas internas. Mismo design
 * language que la landing v3 (Tailwind navy/slate/emerald, bordes 2xl,
 * Inter). Conviértelos en imports en cada página para coherencia.
 *
 * No abstrae prematuramente — solo lo que YA se repite 3+ veces.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { ControlRef, SourceRef } from '@assessos/shared';

// ─────────────────────────────────────────────────────────────────────
// PageHeader — título + subtítulo + acciones (botones a la derecha)
// ─────────────────────────────────────────────────────────────────────

export interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function PageHeader({ eyebrow, title, subtitle, actions }: PageHeaderProps): JSX.Element {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-1">
        {eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
            {eyebrow}
          </p>
        )}
        <h1 className="text-2xl md:text-3xl font-bold text-brand-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 leading-relaxed">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────
// StatCard — KPI card con label uppercase + valor grande + delta opcional
// ─────────────────────────────────────────────────────────────────────

export type StatTone = 'brand' | 'emerald' | 'amber' | 'rose' | 'slate';

const STAT_VALUE_COLOR: Record<StatTone, string> = {
  brand: 'text-brand-900',
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  rose: 'text-rose-700',
  slate: 'text-slate-700',
};

export interface StatCardProps {
  label: string;
  value: string;
  delta?: string;
  tone?: StatTone;
}

export function StatCard({ label, value, delta, tone = 'brand' }: StatCardProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        {label}
      </p>
      <p className={`mt-2 text-3xl font-bold tracking-tight ${STAT_VALUE_COLOR[tone]}`}>{value}</p>
      {delta && <p className="mt-1 text-xs text-slate-500 leading-relaxed">{delta}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Card — wrapper estándar con padding + border + opcional header
// ─────────────────────────────────────────────────────────────────────

export interface CardProps {
  title?: string;
  hint?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Card({ title, hint, actions, children, className = '' }: CardProps): JSX.Element {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-6 ${className}`}>
      {(title !== undefined || actions !== undefined) && (
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title && <h2 className="text-base font-semibold text-brand-900">{title}</h2>}
            {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
          </div>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EmptyState — cuando una lista está vacía. Icono + título + descripción + CTA
// ─────────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  cta?: { label: string; to: string };
}

export function EmptyState({ icon, title, description, cta }: EmptyStateProps): JSX.Element {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
      {icon && (
        <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-slate-400">
          {icon}
        </div>
      )}
      <p className="text-base font-semibold text-brand-900">{title}</p>
      {description && (
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {cta && (
        <Link
          to={cta.to}
          className="mt-5 inline-flex items-center rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700/90"
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Button — primary y secondary consistentes
// ─────────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-700 text-white hover:bg-brand-700/90',
  secondary:
    'border border-slate-200 bg-white text-brand-900 hover:bg-slate-50 hover:border-slate-300',
  ghost: 'text-brand-700 hover:bg-brand-50',
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-3 text-sm',
};

export interface ButtonLinkProps {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function ButtonLink({
  to,
  children,
  variant = 'primary',
  size = 'md',
}: ButtonLinkProps): JSX.Element {
  return (
    <Link to={to} className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${BUTTON_SIZE[size]}`}>
      {children}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Badge — etiquetas pequeñas con color por tono
// ─────────────────────────────────────────────────────────────────────

export type BadgeTone = 'brand' | 'emerald' | 'amber' | 'rose' | 'slate' | 'purple' | 'blue';

// WCAG AA fix (Track A): patrón bg-{tone}-100 + text-{tone}-900 garantiza
// contraste ≥ 7:1 en todos los tonos. El patrón anterior bg-50/text-700
// no cumplía AA en emerald (4.81:1) ni amber (4.5:1 límite) sobre fondos
// blancos de tarjetas anidadas. Verificado contra WebAIM Contrast Checker.
const BADGE_TONE: Record<BadgeTone, string> = {
  brand: 'bg-brand-100 text-brand-900',
  emerald: 'bg-emerald-100 text-emerald-900',
  amber: 'bg-amber-100 text-amber-900',
  rose: 'bg-rose-100 text-rose-900',
  slate: 'bg-slate-100 text-slate-900',
  purple: 'bg-purple-100 text-purple-900',
  blue: 'bg-blue-100 text-blue-900',
};

export function Badge({
  children,
  tone = 'slate',
}: {
  children: ReactNode;
  tone?: BadgeTone;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${BADGE_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LoadingSkeleton — placeholder de cards mientras carga
// ─────────────────────────────────────────────────────────────────────

export function LoadingSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <ul className="space-y-3" aria-busy="true" role="status" aria-label="Cargando">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="h-4 w-1/3 rounded bg-slate-200 animate-pulse" />
          <div className="mt-2 h-3 w-1/4 rounded bg-slate-100 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SearchInput — input estándar con icono lupa
// ─────────────────────────────────────────────────────────────────────

export interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export function SearchInput({ value, onChange, placeholder }: SearchInputProps): JSX.Element {
  return (
    <div className="relative flex-1">
      <span
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        aria-hidden
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-4 w-4"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="M14 14l4 4" strokeLinecap="round" />
        </svg>
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ScoreBar — barra horizontal con label + valor + colored fill por score
// ─────────────────────────────────────────────────────────────────────

export function scoreColor(score: number): string {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 50) return 'bg-amber-500';
  return 'bg-rose-500';
}

export function scoreTextColor(score: number): string {
  // emerald-700 sobre white = ~5.6:1 ✓ (AA pass)
  // amber-700 sobre white = ~4.5:1 (límite exacto, depende de pantalla);
  //   se sube a amber-800 (~6.3:1) para margen de seguridad. ADR-024.
  // rose-700 sobre white = ~5.9:1 ✓ (AA pass)
  if (score >= 75) return 'text-emerald-700';
  if (score >= 50) return 'text-amber-800';
  return 'text-rose-700';
}

export interface ScoreBarProps {
  label: string;
  score: number;
  hint?: string;
}

export function ScoreBar({ label, score, hint }: ScoreBarProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-slate-700">
          {label}
          {hint && <span className="ml-2 text-slate-400 font-normal">{hint}</span>}
        </span>
        <span className={`font-semibold ${scoreTextColor(score)}`}>{score.toFixed(1)}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${scoreColor(score)}`}
          style={{ width: `${String(clamped)}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ProgressBar — barra lineal con porcentaje opcional. Para sticky bars.
// ─────────────────────────────────────────────────────────────────────

export interface ProgressBarProps {
  percent: number;
  tone?: 'brand' | 'emerald' | 'amber';
  size?: 'sm' | 'md';
}

export function ProgressBar({
  percent,
  tone = 'brand',
  size = 'md',
}: ProgressBarProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, percent));
  const bg = {
    brand: 'bg-brand-700',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
  }[tone];
  const h = size === 'sm' ? 'h-1' : 'h-1.5';
  return (
    <div className={`${h} w-full rounded-full bg-slate-100 overflow-hidden`}>
      <div
        className={`${h} rounded-full transition-all duration-300 ${bg}`}
        style={{ width: `${String(clamped)}%` }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ProgressRing — donut SVG con porcentaje al centro
// ─────────────────────────────────────────────────────────────────────

export interface ProgressRingProps {
  percent: number;
  size?: number; // px
  stroke?: number;
  label?: string;
  tone?: 'brand' | 'emerald' | 'amber' | 'rose';
}

export function ProgressRing({
  percent,
  size = 96,
  stroke = 8,
  label,
  tone = 'brand',
}: ProgressRingProps): JSX.Element {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent));
  const dash = (clamped / 100) * circumference;
  const colorClass = {
    brand: 'text-brand-700',
    emerald: 'text-emerald-500',
    amber: 'text-amber-500',
    rose: 'text-rose-500',
  }[tone];
  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-slate-100"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className={`transition-all duration-500 ${colorClass}`}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-brand-900 tracking-tight">{clamped}%</span>
        {label && <span className="mt-0.5 text-[10px] uppercase text-slate-400">{label}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ConnectionBadge — online/offline visible siempre en top bar
// ─────────────────────────────────────────────────────────────────────

export function ConnectionBadge({ online }: { online: boolean }): JSX.Element {
  return (
    <span
      // WCAG AA fix: text-emerald-900 sobre bg-emerald-50 da ~9.1:1;
      // text-amber-900 sobre bg-amber-50 da ~8.3:1 — ambos superan AA.
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        online
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}
      title={online ? 'Conectado, sincronizando' : 'Modo offline — todo se guarda local'}
    >
      <span className={`relative flex h-2 w-2`} aria-hidden>
        <span
          className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
            online ? 'animate-ping bg-emerald-400' : 'bg-amber-400'
          }`}
        />
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${
            online ? 'bg-emerald-500' : 'bg-amber-500'
          }`}
        />
      </span>
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QuestionCard — wrapper de cada pregunta en el capture
// Tipo + label + required badge + body (rendered field) + footer
// ─────────────────────────────────────────────────────────────────────

export type QuestionStatus = 'answered' | 'required-pending' | 'pending' | 'error';

const STATUS_RING: Record<QuestionStatus, string> = {
  answered: 'border-emerald-200 bg-emerald-50/30',
  'required-pending': 'border-amber-200',
  pending: 'border-slate-200',
  error: 'border-rose-300 bg-rose-50/30',
};

const STATUS_DOT: Record<QuestionStatus, string> = {
  answered: 'bg-emerald-500',
  'required-pending': 'bg-amber-400',
  pending: 'bg-slate-300',
  error: 'bg-rose-500',
};

export interface QuestionCardProps {
  index: number;
  total: number;
  label: string;
  description?: string | undefined;
  typeBadge?: string | undefined;
  required?: boolean | undefined;
  status: QuestionStatus;
  /** Mapeo a controles normativos (ADR-012) — render colapsable junto a sources. */
  controls?: ControlRef[] | undefined;
  /** Citas bibliograficas / guias (S17-A) — render colapsable junto a controls. */
  sources?: SourceRef[] | undefined;
  /**
   * ADR-040 D5 · Si true, muestra el badge "dato del cliente" indicando que
   * el valor fue pre-llenado desde el insumo de captura del cliente. El auditor
   * debe verificar y confirmar (o corregir) el valor antes de cerrar.
   */
  prefillBadge?: boolean | undefined;
  children: ReactNode;
}

export function QuestionCard({
  index,
  total,
  label,
  description,
  typeBadge,
  required,
  status,
  controls,
  sources,
  prefillBadge,
  children,
}: QuestionCardProps): JSX.Element {
  const totalRefs = (controls?.length ?? 0) + (sources?.length ?? 0);
  return (
    <article
      className={`rounded-2xl border-2 bg-white p-5 transition-colors ${STATUS_RING[status]}`}
    >
      <header className="mb-3 flex items-start gap-3">
        <span className="mt-1.5 flex shrink-0 items-center gap-2 text-xs font-mono text-slate-400">
          <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[status]}`} aria-hidden />
          {String(index).padStart(2, '0')}/{String(total).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-brand-900 leading-snug">{label}</h3>
            {required && <Badge tone="rose">Requerido</Badge>}
            {typeBadge && <Badge tone="slate">{typeBadge}</Badge>}
            {prefillBadge && (
              <span
                data-testid="prefill-badge"
                className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
                title="Este valor fue pre-llenado desde el formulario del cliente. Verifícalo y ajústalo si es necesario."
              >
                dato del cliente
              </span>
            )}
          </div>
          {description && (
            <p className="mt-1 text-sm text-slate-500 leading-relaxed">{description}</p>
          )}
          {totalRefs > 0 && <QuestionReferences controls={controls} sources={sources} />}
        </div>
      </header>
      <div>{children}</div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QuestionReferences — bloque colapsable con controles + fuentes
// Distingue visualmente:
//   • Controles = mapeo a framework normativo (ISO 27001 A.5.1, etc.)
//   • Fuentes   = cita bibliografica de apoyo (guia NQA, ISO-info, etc.)
// ─────────────────────────────────────────────────────────────────────

function QuestionReferences({
  controls,
  sources,
}: {
  controls?: ControlRef[] | undefined;
  sources?: SourceRef[] | undefined;
}): JSX.Element | null {
  const total = (controls?.length ?? 0) + (sources?.length ?? 0);
  if (total === 0) return null;
  return (
    <details className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 group">
      <summary className="cursor-pointer select-none px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 list-none flex items-center gap-1.5">
        <span className="inline-block transition-transform group-open:rotate-90" aria-hidden>
          ▸
        </span>
        <span>Fuentes y controles ({total})</span>
      </summary>
      <div className="border-t border-slate-200 px-3 py-2 space-y-2">
        {controls && controls.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Controles ({controls.length})
            </p>
            <ul className="space-y-1">
              {controls.map((c, i) => (
                <li key={`c-${i}`} className="text-xs leading-relaxed text-slate-700">
                  <span className="inline-block rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-mono text-brand-800 mr-1.5">
                    {c.framework}
                  </span>
                  <span className="font-medium">{c.id}</span>
                  {c.description && <span className="text-slate-500"> — {c.description}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {sources && sources.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Fuentes ({sources.length})
            </p>
            <ul className="space-y-1">
              {sources.map((s, i) => (
                <li key={`s-${i}`} className="text-xs leading-relaxed text-slate-700">
                  <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-mono text-amber-800 mr-1.5">
                    {s.source}
                  </span>
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-brand-700 hover:underline"
                    >
                      {s.citation}
                    </a>
                  ) : (
                    <span>{s.citation}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </details>
  );
}
